"use client";

import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import {
  depthHistorySamples,
  mergeDepthSamples,
  nextDepthSamples,
  type OrderBookDepthCoverage,
  type OrderBookDepthSample
} from "@/lib/orderbook-depth";
import { DATA_REFRESHED_EVENT } from "@/lib/data-refresh";
import { requireLiveMarketSource } from "@/lib/live-market-data";
import type { BinanceKlineBar, BinanceKlineResponse, BinanceOrderBookRatio } from "@/lib/types";

const OrderBookDepthChart = lazy(async () => {
  const module = await import("./orderbook-depth-chart");
  return { default: module.OrderBookDepthChart };
});

const EMPTY_BANDS: BinanceOrderBookRatio["bands"] = [];
const HISTORY_LIMIT = 500;
const POLL_INTERVAL_MS = 10_000;

export function BidAskDepthWidget() {
  const mountRef = useRef<HTMLDivElement>(null);
  const inFlightRef = useRef<Promise<void> | null>(null);
  const [ready, setReady] = useState(false);
  const [bands, setBands] = useState<BinanceOrderBookRatio["bands"]>(EMPTY_BANDS);
  const [coverage, setCoverage] = useState<OrderBookDepthCoverage>();
  const [samples, setSamples] = useState<OrderBookDepthSample[]>([]);
  const [priceBars, setPriceBars] = useState<BinanceKlineBar[]>([]);
  const [depthSource, setDepthSource] = useState("");
  const [priceSource, setPriceSource] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshedAt, setRefreshedAt] = useState<string>();

  const loadDepth = useCallback((foreground = false) => {
    if (inFlightRef.current) return inFlightRef.current;

    const request = (async () => {
      if (foreground) setLoading(true);
      try {
        const depthQuery = new URLSearchParams({
          symbol: "BTCUSDT",
          market: "spot",
          levels: "5,10",
          interval: "4h",
          limit: String(HISTORY_LIMIT)
        });
        const klineQuery = new URLSearchParams({
          symbol: "BTCUSDT",
          market: "spot",
          interval: "4h",
          limit: String(HISTORY_LIMIT)
        });
        const [depthResponse, klineResponse] = await Promise.all([
          fetch(`/api/binance/orderbook-ratio?${depthQuery}`, { cache: "no-store", credentials: "same-origin" }),
          fetch(`/api/binance/klines?${klineQuery}`, { cache: "no-store", credentials: "same-origin" })
        ]);
        if (!depthResponse.ok) throw new Error("Order-book depth is temporarily unavailable.");
        if (!klineResponse.ok) throw new Error("BTC price history is temporarily unavailable.");

        const [payload, nextPricePayload] = await Promise.all([
          depthResponse.json() as Promise<BinanceOrderBookRatio>,
          klineResponse.json() as Promise<BinanceKlineResponse>
        ]);
        const nextDepthSource = requireLiveMarketSource(payload.data_origin, payload.source, "Order-book depth");
        const nextPriceSource = requireLiveMarketSource(nextPricePayload.data_origin, nextPricePayload.source, "BTC price history");
        if (!Array.isArray(payload.bands) || !payload.bands.length || !Array.isArray(payload.history) || !payload.history.length) {
          throw new Error("Order-book depth returned no live observations.");
        }
        if (!Array.isArray(nextPricePayload.bars) || !nextPricePayload.bars.length) {
          throw new Error("BTC price history returned no live observations.");
        }

        const observedAt = payload.observation_at ?? payload.collected_at ?? new Date().toISOString();
        const live = nextDepthSamples([], payload.bands ?? EMPTY_BANDS, new Date(observedAt).toLocaleTimeString(), 1, {
          collectedAt: observedAt,
          price: payload.mid
        });
        const incoming = [...depthHistorySamples(payload.history ?? []), ...live];

        setBands(payload.bands);
        setCoverage(payload.coverage);
        setSamples((previous) => mergeDepthSamples(previous, incoming, HISTORY_LIMIT));
        setDepthSource(nextDepthSource);
        setPriceBars(nextPricePayload.bars);
        setPriceSource(nextPriceSource);
        setRefreshedAt(payload.collected_at ?? observedAt);
        setError("");
      } catch (reason) {
        setError(reason instanceof Error ? reason.message : "Order-book depth is temporarily unavailable.");
      } finally {
        setLoading(false);
      }
    })();

    inFlightRef.current = request;
    void request.finally(() => {
      if (inFlightRef.current === request) inFlightRef.current = null;
    });
    return request;
  }, []);

  useEffect(() => {
    const node = mountRef.current;
    if (!node || ready) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setReady(true);
          observer.disconnect();
        }
      },
      { rootMargin: "320px 0px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [ready]);

  useEffect(() => {
    if (!ready) return;
    void loadDepth(true);
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") void loadDepth();
    }, POLL_INTERVAL_MS);
    const reload = () => void loadDepth();
    window.addEventListener(DATA_REFRESHED_EVENT, reload);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener(DATA_REFRESHED_EVENT, reload);
    };
  }, [loadDepth, ready]);

  return (
    <div ref={mountRef} className="min-h-[920px] md:min-h-[960px]">
      {ready ? (
        <Suspense fallback={<div className="h-[920px] animate-pulse border border-[var(--hc-border)] bg-[var(--hc-bg)] md:h-[960px]" />}>
          <OrderBookDepthChart
            bands={bands}
            coverage={coverage}
            depthSource={depthSource}
            error={error}
            loading={loading}
            market="spot"
            onRefresh={() => void loadDepth(true)}
            priceBars={priceBars}
            priceSource={priceSource}
            refreshedAt={refreshedAt}
            samples={samples}
            symbol="BTCUSDT"
            timeframe="4h"
          />
        </Suspense>
      ) : (
        <div className="grid h-[920px] place-items-center border border-dashed border-[var(--hc-border-strong)] bg-[var(--hc-bg)] px-4 text-center text-sm text-[var(--hc-muted)] md:h-[960px]">
          Order-book chart loads when this panel approaches the viewport.
        </div>
      )}
    </div>
  );
}
